const DB_NAME = 'redsorm_local_db';
// Bumped to version 6 to ensure upgrade from original database
const DB_VERSION = 6;

// Do NOT cache the promise at module level — re-open every time so that if
// a version bump is needed it always fires onupgradeneeded correctly.
let dbInstance = null;

const initDb = () => {
  if (dbInstance && dbInstance.version >= DB_VERSION) {
    return Promise.resolve(dbInstance);
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create every store if it does not already exist.
      // This runs for brand-new DBs (v0→v2) AND for existing v1 DBs that
      // were created before orders_queue was added.
      const stores = [
        { name: 'menu',            keyPath: 'id' },
        { name: 'tables',          keyPath: 'id' },
        { name: 'categories',      keyPath: 'id' },
        { name: 'orders',          keyPath: 'id' },
        { name: 'orders_queue',    keyPath: 'id' },
        { name: 'api_mutations_queue', keyPath: 'id' },
        { name: 'company_profile', keyPath: 'key' },
        { name: 'ingredients',     keyPath: 'id' },
        { name: 'staff',           keyPath: 'id' },
        { name: 'customer_settings', keyPath: 'key' },
        { name: 'analytics_cache', keyPath: 'key' },
      ];

      stores.forEach(({ name, keyPath }) => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath });
          console.log(`[IndexedDB] Created store: ${name}`);
        }
      });
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;

      // Handle situations where another tab tries to upgrade the DB
      dbInstance.onversionchange = () => {
        dbInstance.close();
        dbInstance = null;
        window.location.reload();
      };

      // If any store is somehow still missing (shouldn't happen after bump),
      // log clearly so it can be debugged.
      ['orders_queue', 'orders', 'menu', 'tables', 'categories'].forEach(store => {
        if (!dbInstance.objectStoreNames.contains(store)) {
          console.error(`[IndexedDB] CRITICAL: store "${store}" is missing from DB!`);
        }
      });

      resolve(dbInstance);
    };

    request.onerror = (event) => {
      console.error('[IndexedDB] Database open error:', event.target.error);
      reject(event.target.error);
    };

    request.onblocked = () => {
      console.warn('[IndexedDB] Database upgrade blocked — close all other tabs and reload.');
      alert("Database is upgrading. Please close other tabs of this application and click OK to reload.");
      window.location.reload();
      reject(new Error("Database upgrade blocked"));
    };
  });
};

// ─── General helpers ──────────────────────────────────────────────────────────

export const saveToStore = async (storeName, data) => {
  const db = await initDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    if (Array.isArray(data)) {
      data.forEach(item => store.put(item));
    } else {
      store.put(data);
    }

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (e) => reject(e.target.error);
  });
};

export const syncStoreWithServer = async (storeName, serverData) => {
  const db = await initDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    const request = store.getAllKeys();
    request.onsuccess = () => {
      const existingKeys = request.result || [];
      const serverIds = new Set(serverData.map(item => item.id || item.key));
      
      existingKeys.forEach(key => {
        if (!serverIds.has(key) && !(typeof key === 'string' && key.startsWith('temp_'))) {
          store.delete(key);
        }
      });
      
      serverData.forEach(item => store.put(item));
    };

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (e) => reject(e.target.error);
  });
};

export const getAllFromStore = async (storeName) => {
  const db = await initDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = (e) => reject(e.target.error);
  });
};

export const getFromStore = async (storeName, key) => {
  const db = await initDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(e.target.error);
  });
};

export const deleteFromStore = async (storeName, key) => {
  const db = await initDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onsuccess = () => resolve(true);
    request.onerror = (e) => reject(e.target.error);
  });
};

export const clearStore = async (storeName) => {
  const db = await initDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve(true);
    request.onerror = (e) => reject(e.target.error);
  });
};

// ─── Queue helpers ────────────────────────────────────────────────────────────

export const addToQueue = async (order) => {
  const uuid = `offline_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
  const queuedOrder = {
    order_status: 'pending',
    ...order,
    id: uuid,
    order_id: uuid,
    is_offline: true,
    created_at: order.created_at || new Date().toISOString(),
  };
  await saveToStore('orders_queue', queuedOrder);
  console.log('[IndexedDB] Queued offline order:', uuid, queuedOrder);
  return queuedOrder;
};

export const getQueue = async () => {
  return await getAllFromStore('orders_queue');
};

export const removeFromQueue = async (orderId) => {
  return await deleteFromStore('orders_queue', orderId);
};
